import { useMemo } from 'react'
import { Divider } from '@mui/material'
import { BannedUser } from '@weblite/ts-action/subscription'
import {
  useClientUserPermissions,
  useHallRemovedSubscribers,
  useSearchedRemovedSubscribers,
} from '$logic/subscription/react-hook'
import { AddUserIcon } from '$common/icons'
import { MenuIcon } from '$common/icons/menu'
import { UserListItem, UserListItemSkeleton } from '$common/user-list-item'
import { withSearchableListPage } from '$common/searchable-list-page'
import { EmptyStateList } from '$common/empty-state-list'
import { t } from '$i18n'
import { getUserDisplayName, useUser } from '$logic/users'
import { closeHallRemovedUsers, openHallRemoveMember } from '$logic/router'
import { showRemovedUserMenu } from '$comp/modals/menus/removed-user-menu'

function ListItem({ id, creatorId, hallId }: BannedUser & { hallId: string }) {
  const { user: deleterUser } = useUser(creatorId)
  return (
    <>
      <UserListItem
        userId={id}
        hasDivider={false}
        secondaryText={`${t(
          'profile.hall.removeUsers.removedBy',
        )} ${getUserDisplayName(deleterUser)}`}
        SecondaryActionIcon={MenuIcon}
        onSecondaryActionIconClick={() =>
          showRemovedUserMenu({ userId: id, roomId: hallId })
        }
      />
      <Divider variant="inset" />
    </>
  )
}

export function RemovedUsers({ roomId }: { roomId: string }) {
  const clientUserPermissions = useClientUserPermissions(roomId)
  const Page = useMemo(
    () =>
      withSearchableListPage<
        BannedUser,
        string,
        { query: string; fetchedItems: BannedUser[] }
      >({
        appBarTitle: t('profile.hall.actions.removedUsers'),
        ListItem: item => <ListItem {...item} hallId={roomId} />,
        SkeletonItem: UserListItemSkeleton,
        EmptyStateComponent: () => <EmptyStateList />,
        fetchHook: () =>
          useHallRemovedSubscribers(roomId, 'removedSubscribers'),
        searchHook: useSearchedRemovedSubscribers,
        actionButtons: [
          {
            text: t('profile.hall.removeUsers.title'),
            icon: <AddUserIcon color="secondary" />,
            onClick: () => openHallRemoveMember(roomId),
          },
        ],
        onBack: closeHallRemovedUsers,
      }),
    [roomId],
  )

  return (
    <Page
      fetchHookParams={roomId}
      getSearchHookParams={(query, fetchedItems) => ({ query, fetchedItems })}
      hideActionButton={!clientUserPermissions?.banUsers}
    />
  )
}
